import { LoadingButton } from "@mui/lab";
import { Alert, type ButtonProps, Link, Stack } from "@mui/material";
import type { ExistingProductsResp } from "adl-gen/ferovinum/app/api";
import type { ProductQuantity } from "adl-gen/ferovinum/app/common";
import { HttpServiceError } from "adl-service/http-service-error";
import { PRODUCT_QUANTITY_CSV_TEMPLATE_URL } from "components/assets/url";
import type { ParseResult } from "papaparse";
import { useCallback, useMemo, useState } from "react";
import { useCSVReader } from "react-papaparse";
import {
  ProductQuantityCsvProcessor,
  type ProductQuantityUploadResult,
} from "./product-quantity-csv-processor";
import { ProductQuantityCsvUploadDialog } from "./product-quantity-csv-upload-dialog";
import { ProductQuantityErrorAccordion } from "./product-quantity-error-accordion";

type UploadResult =
  | { type: "error"; message: string }
  | { type: "success"; errorResult: ProductQuantityUploadResult }
  | { type: "success" };

export const ProductQuantityCsvUpload = ({
  buttonLabel,
  getProductsByCode,
  onSuccessfulUpload,
  onCancelUpload,
  postUploadValidator,
}: {
  buttonLabel: string;
  getProductsByCode: (productCodes: string[]) => Promise<ExistingProductsResp>;
  onSuccessfulUpload: (products: ProductQuantity[]) => Promise<void>;
  onCancelUpload?: () => Promise<void>;
  postUploadValidator?: (
    uploadResult: ProductQuantityUploadResult,
  ) => Promise<ProductQuantityUploadResult>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult>({
    type: "success",
  });

  const { CSVReader } = useCSVReader();

  const csvProcessor = useMemo(
    () =>
      new ProductQuantityCsvProcessor({
        productExtractor: getProductsByCode,
        postUploadValidator,
      }),
    [getProductsByCode, postUploadValidator],
  );

  const handleCSVUpload = useCallback(
    async (parseResult: ParseResult<string[]>) => {
      setIsLoading(true);
      try {
        const uploadResult =
          await csvProcessor.parseCSVToProductLineItems(parseResult);

        if (
          uploadResult.errors.length === 0 &&
          uploadResult.nonExistingProductCodes.length === 0
        ) {
          const productQuantities = uploadResult.success.map((p) => p.value);
          await onSuccessfulUpload(productQuantities);
          setUploadResult({ type: "success" });
        } else {
          setUploadResult({
            type: "success",
            errorResult: uploadResult,
          });
          setIsUploadDialogOpen(true);
        }
      } catch (error) {
        const errorMessage =
          error instanceof HttpServiceError
            ? error.publicMessage
            : String(error);
        setUploadResult({ type: "error", message: errorMessage });
        setIsUploadDialogOpen(true);
      } finally {
        setIsLoading(false);
      }
    },
    [csvProcessor, onSuccessfulUpload],
  );

  const handleProceed = useCallback(async () => {
    if (uploadResult && "errorResult" in uploadResult) {
      const data = uploadResult.errorResult.success.map((p) => p.value);
      await onSuccessfulUpload(data);
      setIsUploadDialogOpen(false);
    }
  }, [uploadResult, onSuccessfulUpload]);

  const handleCancel = useCallback(async () => {
    if (onCancelUpload) {
      await onCancelUpload();
    }
    setIsUploadDialogOpen(false);
  }, [onCancelUpload]);

  return (
    <>
      <Stack alignItems="center">
        <CSVReader
          onUploadAccepted={handleCSVUpload}
          config={{ skipEmptyLines: "greedy" }}
        >
          {({ getRootProps }: { getRootProps: () => ButtonProps }) => (
            <LoadingButton
              variant="contained"
              {...getRootProps()}
              loading={isLoading}
            >
              {buttonLabel}
            </LoadingButton>
          )}
        </CSVReader>
        <Link href={PRODUCT_QUANTITY_CSV_TEMPLATE_URL} download>
          Download CSV Template
        </Link>
      </Stack>
      <ProductQuantityCsvUploadDialog
        canProceed={uploadResult == null || uploadResult.type === "success"}
        body={<UploadDialogBody result={uploadResult} />}
        isOpen={isUploadDialogOpen}
        onProceed={handleProceed}
        onCancel={handleCancel}
      />
    </>
  );
};

const UploadDialogBody = ({ result }: { result: UploadResult }) => {
  if (result.type === "error") {
    return (
      <Alert severity="error" sx={{ whiteSpace: "pre-wrap" }}>
        {result.message}
      </Alert>
    );
  }

  if ("errorResult" in result) {
    return (
      <>
        <Alert severity="error" sx={{ whiteSpace: "pre-wrap" }}>
          {"Errors were detected. Please review the details below."}
        </Alert>
        <ProductQuantityErrorAccordion uploadResult={result.errorResult} />
      </>
    );
  }

  return (
    <Alert severity="success">All products were successfully uploaded.</Alert>
  );
};
